import * as React from "react"
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react"
import { DayPicker, getDefaultClassNames } from "react-day-picker"
import "react-day-picker/style.css"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

function Calendar({
  className,
  classNames,
  components,
  showOutsideDays = true,
  disabled,
  startMonth,
  month,
  defaultMonth,
  allowPastDates = false,
  ...props
}) {
  const defaultClassNames = getDefaultClassNames()
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const effectiveStartMonth = allowPastDates
    ? startMonth
    : (startMonth && startMonth > today ? startMonth : today)
  const clampMonth = (value) => (
    effectiveStartMonth && value && value < effectiveStartMonth
      ? effectiveStartMonth
      : value
  )
  const disabledMatchers = Array.isArray(disabled)
    ? disabled
    : disabled
      ? [disabled]
      : []

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      disabled={allowPastDates ? disabledMatchers : [{ before: today }, ...disabledMatchers]}
      startMonth={effectiveStartMonth}
      month={clampMonth(month)}
      defaultMonth={clampMonth(defaultMonth)}
      className={cn(
        defaultClassNames.root,
        "w-full p-3 text-sm [--rdp-accent-background-color:hsl(var(--accent))] [--rdp-accent-color:hsl(var(--primary))] [--rdp-day-height:2.25rem] [--rdp-day-width:2.25rem] [--rdp-day_button-height:2.25rem] [--rdp-day_button-width:2.25rem]",
        className
      )}
      classNames={{
        months: cn(defaultClassNames.months, "flex w-full flex-col gap-4 sm:flex-row"),
        month: cn(defaultClassNames.month, "w-full space-y-3"),
        month_caption: cn(defaultClassNames.month_caption, "flex h-9 items-center justify-center px-10"),
        caption_label: cn(defaultClassNames.caption_label, "text-sm font-semibold"),
        nav: cn(defaultClassNames.nav, "inset-x-3 top-3 flex h-9 items-center justify-between"),
        button_previous: cn(
          defaultClassNames.button_previous,
          buttonVariants({ variant: "outline" }),
          "h-8 w-8 bg-background p-0 opacity-70 hover:opacity-100"
        ),
        button_next: cn(
          defaultClassNames.button_next,
          buttonVariants({ variant: "outline" }),
          "h-8 w-8 bg-background p-0 opacity-70 hover:opacity-100"
        ),
        month_grid: cn(defaultClassNames.month_grid, "w-full table-fixed border-collapse"),
        weekdays: cn(defaultClassNames.weekdays, "border-0"),
        weekday: cn(
          defaultClassNames.weekday,
          "h-9 w-9 p-0 text-center text-xs font-medium text-muted-foreground"
        ),
        week: cn(defaultClassNames.week, "border-0"),
        day: cn(
          defaultClassNames.day,
          "relative h-9 w-9 p-0 text-center text-sm focus-within:z-20"
        ),
        day_button: cn(
          defaultClassNames.day_button,
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 rounded-md border-0 p-0 font-normal"
        ),
        selected: cn(
          defaultClassNames.selected,
          "text-sm font-normal [&>button]:bg-primary [&>button]:text-primary-foreground [&>button:hover]:bg-primary"
        ),
        range_start: cn(
          defaultClassNames.range_start,
          "rounded-l-md bg-accent [&>button]:bg-primary [&>button]:text-primary-foreground"
        ),
        range_end: cn(
          defaultClassNames.range_end,
          "rounded-r-md bg-accent [&>button]:bg-primary [&>button]:text-primary-foreground"
        ),
        range_middle: cn(
          defaultClassNames.range_middle,
          "bg-accent text-accent-foreground [&>button]:rounded-none [&>button]:bg-transparent [&>button]:text-accent-foreground"
        ),
        today: cn(defaultClassNames.today, "font-semibold text-primary"),
        outside: cn(defaultClassNames.outside, "text-muted-foreground opacity-40"),
        disabled: cn(defaultClassNames.disabled, "cursor-not-allowed text-muted-foreground opacity-35"),
        hidden: cn(defaultClassNames.hidden, "invisible"),
        ...classNames,
      }}
      components={{
        Chevron: ({ className: chevronClassName, orientation, ...chevronProps }) => {
          const Icon = orientation === "left"
            ? ChevronLeft
            : orientation === "up"
              ? ChevronUp
              : orientation === "down"
                ? ChevronDown
                : ChevronRight
          return <Icon className={cn("h-4 w-4", chevronClassName)} {...chevronProps} />
        },
        ...components,
      }}
      {...props} />
  );
}
Calendar.displayName = "Calendar"

export { Calendar }
